/**
 * Warn when a company create/edit reuses a Y-tunnus (#2410).
 *
 * A chain's branches may share one business id, so this does not block the
 * save. The operator either continues with a company already on file or
 * confirms that this row is its own branch. A name score at or above 0.9
 * (likelyTypo, computed by the API) is called out as a probable typo.
 */

import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import type { BusinessIdMatch, Company } from '@/hooks/useCompanies';

interface BusinessIdConflictDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  businessId: string;
  matches: readonly BusinessIdMatch[];
  onUseExisting: (company: Company) => void;
  onConfirmBranch: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

function branchDetails(company: Company): string | null {
  const locality = [company.postalCode, company.city].filter(Boolean).join(' ');
  const parts = [company.streetAddress, locality || null, company.phone].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

export function BusinessIdConflictDialog({
  open,
  mode,
  businessId,
  matches,
  onUseExisting,
  onConfirmBranch,
  onCancel,
  isSaving,
}: BusinessIdConflictDialogProps) {
  const { t } = useTranslation('companies');
  const { t: tc } = useTranslation('common');

  const ranked = [...matches].sort((a, b) => {
    const byScore = (b.similarity ?? -1) - (a.similarity ?? -1);
    if (byScore !== 0) return byScore;
    return a.company.name.localeCompare(b.company.name, 'fi');
  });
  const closestTypo = ranked.find((match) => match.likelyTypo);
  const first = ranked[0];

  return (
    <ResponsiveDialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      mobileTitle={t('businessIdConflict.title')}
    >
      <DialogTitle>{t('businessIdConflict.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">
            {ranked.length === 1 && first
              ? t('businessIdConflict.introOne', { businessId, name: first.company.name })
              : t('businessIdConflict.introMany', { businessId, count: ranked.length })}
          </Typography>

          {closestTypo?.similarity === 1 && (
            <Alert severity="warning">
              {t('businessIdConflict.sameName', { name: closestTypo.company.name })}
            </Alert>
          )}
          {closestTypo && closestTypo.similarity != null && closestTypo.similarity < 1 && (
            <Alert severity="warning">
              {t('businessIdConflict.likelyTypo', {
                name: closestTypo.company.name,
                percent: Math.round(closestTypo.similarity * 100),
              })}
            </Alert>
          )}

          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {ranked.map((match) => {
              const details = branchDetails(match.company);
              return (
                <Stack
                  key={match.company.id}
                  component="li"
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                  sx={{ py: 1.25, borderTop: 1, borderColor: 'divider' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: match.likelyTypo ? 700 : 500,
                        color: match.likelyTypo ? 'warning.main' : 'text.primary',
                      }}
                    >
                      {match.company.name}
                    </Typography>
                    {match.likelyTypo && (
                      <Typography variant="caption" color="warning.main" display="block">
                        {t('businessIdConflict.typoBadge')}
                      </Typography>
                    )}
                    {details && (
                      <Typography variant="body2" color="text.secondary">
                        {details}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isSaving}
                    onClick={() => onUseExisting(match.company)}
                  >
                    {t('businessIdConflict.useExisting')}
                  </Button>
                </Stack>
              );
            })}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={isSaving}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" color="warning" disabled={isSaving} onClick={onConfirmBranch}>
          {mode === 'edit'
            ? t('businessIdConflict.saveBranch')
            : t('businessIdConflict.createBranch')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
